import "./App.css";
import { Switch, Route, Redirect } from "wouter";
import { AuthProvider } from "./contexts/auth/auth-context";
import { CartProvider, MarketplaceLayout, ProductProvider } from "./features/marketplace";
import { PaddleProvider } from "./contexts/paddle/paddle-context";
import { MusioClientProvider } from "./contexts/client/musio-client-context";
import { PortalConfigProvider } from "./contexts/portal-config/portal-config-context";
import { WithLayouts } from "./layouts/with-layouts";
import BackgroundLayout from "./layouts/background-layout";
import { AccountLayout } from "./features/account";
import MainNavLayout from "./layouts/main-nav-layout";
import AccountPage from "./routes/account/account";
import MarketplacePage from "./routes/marketplace/marketplace";
import CollectionPage from "./routes/marketplace/collection";
import LoginPage from "./routes/login-signup/login";
import SignupPage from "./routes/login-signup/signup";
import ForgotPasswordPage from "./routes/reset/forgot-password";
import RecoverPassword from "./routes/recover/recover-password";
import CancelPassword from "./routes/recover/cancel-recover-password";
import Error404 from "./routes/errors/404";
import ResetEmail from "./routes/reset/reset-email";
import CancelEmail from "./routes/reset/cancel-email";
import DeveloperPage from "./routes/developer/developer";
import VerifyPage from "./routes/login-signup/verify";
import ErrorPage from "./routes/errors/error";
import ConfirmEmailPage from "./routes/login-signup/confirm-email";
import { Helmet, HelmetProvider } from "@dr.pogodin/react-helmet";
import AccountRedirectCheck from "./features/account/utils/redirect";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BannerProvider } from "@/features/banner";

const queryClient = new QueryClient();

function App() {

  return (
    <QueryClientProvider client={queryClient}>
      <HelmetProvider>
        <Helmet>
          <title>Musio Portal</title>
          <meta name="description" content="Shop for the world's best virtual instruments built for the Musio plugin" />
        </Helmet>
        <MusioClientProvider>
          <PortalConfigProvider>
            <AuthProvider>
              <CartProvider>
                <PaddleProvider>
                  <BannerProvider>
                    <Switch>

                      <Route path="/">
                        <Redirect href="/login" />
                      </Route>

                      <Route path="/account" nest>
                        <Route path="/">
                          <WithLayouts
                            layouts={[BackgroundLayout, MainNavLayout, AccountLayout, AccountRedirectCheck]}
                            page={AccountPage}
                          />
                        </Route>

                        <Route path="/:tab">
                          <WithLayouts
                            layouts={[BackgroundLayout, MainNavLayout, AccountLayout]}
                            page={AccountPage}
                          />
                        </Route>
                      </Route>

                      <Route path="/login">
                        <WithLayouts layouts={[BackgroundLayout, MainNavLayout]} page={LoginPage} />
                      </Route>

                      <Route path="/signup">
                        <WithLayouts layouts={[BackgroundLayout, MainNavLayout]} page={SignupPage} />
                      </Route>

                      <Route path="/verify">
                        <WithLayouts layouts={[BackgroundLayout, MainNavLayout]} page={VerifyPage} />
                      </Route>

                      <Route path="/confirm-email">
                        <WithLayouts layouts={[BackgroundLayout, MainNavLayout]} page={ConfirmEmailPage} />
                      </Route>

                      <Route path="/recover" nest>

                        <Route path="/">
                          <WithLayouts layouts={[BackgroundLayout, MainNavLayout]} page={ForgotPasswordPage} />
                        </Route>

                        <Route path="/reset">
                          <WithLayouts layouts={[BackgroundLayout]} page={RecoverPassword} />
                        </Route>

                        <Route path="/cancel">
                          <WithLayouts layouts={[BackgroundLayout]} page={CancelPassword} />
                        </Route>
                      </Route>

                      <Route path="/email" nest>
                        <Route path="/">
                          <WithLayouts layouts={[BackgroundLayout]} page={ResetEmail} />
                        </Route>

                        <Route path="/cancel">
                          <WithLayouts layouts={[BackgroundLayout]} page={CancelEmail} />
                        </Route>
                      </Route>

                      <Route path="/404">
                        <WithLayouts
                          layouts={[BackgroundLayout]}
                          page={Error404}
                        />
                      </Route>

                      <Route path="/error">
                        <WithLayouts
                          layouts={[BackgroundLayout]}
                          page={ErrorPage}
                        />
                      </Route>

                      <Route path="/marketplace" nest>
                        <ProductProvider>
                          <Route path="/">
                            <WithLayouts
                              layouts={[BackgroundLayout, MainNavLayout, MarketplaceLayout]}
                              page={MarketplacePage}
                            />
                          </Route>

                          <Route path="/collection/:collectionSlug">
                            <WithLayouts
                              layouts={[BackgroundLayout, MainNavLayout, MarketplaceLayout]}
                              page={CollectionPage}
                            />
                          </Route>

                          <Route path="/developer/:developerSlug">
                            <WithLayouts
                              layouts={[BackgroundLayout, MainNavLayout, MarketplaceLayout]}
                              page={DeveloperPage}
                            />
                          </Route>
                        </ProductProvider>
                      </Route>

                      <Route path="*">
                        {() => <Redirect to="/404" />}
                      </Route>

                    </Switch>
                  </BannerProvider>
                </PaddleProvider >
              </CartProvider >
            </AuthProvider >
          </PortalConfigProvider >
        </MusioClientProvider >
      </HelmetProvider >
    </QueryClientProvider >
  );
}

export default App;

